import { firstName } from '@hudhuria/shared';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { meQuery, useMe } from '~/api/queries';
import { PrimaryButton, Radio } from '~/components/ui';
import { signInPath } from '~/lib/entry';
import { session } from '~/session/session';

export const Route = createFileRoute('/center')({
  beforeLoad: async ({ context }) => {
    if (!session.get().token) throw redirect({ to: signInPath() });
    const me =
      context.queryClient.getQueryData(meQuery.queryKey) ??
      (await context.queryClient.fetchQuery({ ...meQuery, retry: false }));
    if (me.centers.length === 1) {
      session.setCenter(me.centers[0]!.id);
      throw redirect({ to: '/hub' });
    }
  },
  component: ChooseCenter,
});

/** "Where are you today?" — shown when a staff member works at more than one center. */
function ChooseCenter() {
  const navigate = useNavigate();
  const me = useMe().data;
  const [selected, setSelected] = useState(() => session.get().centerId ?? me?.centers[0]?.id ?? null);
  if (!me) return null;
  const center = me.centers.find((c) => c.id === selected);

  return (
    <div className="flex min-h-0 flex-1 animate-fade-in flex-col px-[22px] pt-safe-10 pb-safe">
      <div className="text-[14px] text-slate-500">Hi {firstName(me.staff.name)},</div>
      <div className="mt-0.5 font-display text-[28px] font-bold tracking-[-0.02em]">Where are you today?</div>
      <div className="mt-2 text-[14px] text-slate-500">You can switch centers any time from Me.</div>
      <div className="mt-6 -mx-1 flex min-h-0 flex-1 flex-col gap-[10px] px-1 pb-4 scroll-y">
        {me.centers.map((c) => {
          const on = c.id === selected;
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => setSelected(c.id)}
              className="flex cursor-pointer items-center gap-[13px] rounded-2xl px-[15px] py-[14px] text-left"
              style={{
                border: `1.5px solid ${on ? '#0369a1' : '#e8ecf3'}`,
                background: on ? '#f0f8fd' : '#fff',
              }}
            >
              <div
                className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl font-display text-[14px] font-bold"
                style={{ background: on ? '#0369a1' : '#f3f5f9', color: on ? '#fff' : '#475569' }}
              >
                {c.code}
              </div>
              <div className="flex-1">
                <div className="text-[15px] font-semibold">{c.name}</div>
                <div className="mt-px text-[12.5px] text-slate-400">{c.enrolled} students</div>
              </div>
              <Radio on={on} />
            </button>
          );
        })}
      </div>
      <PrimaryButton
        disabled={!center}
        onClick={() => {
          if (!center) return;
          session.setCenter(center.id);
          void navigate({ to: '/hub', replace: true });
        }}
      >
        Continue to {center?.name ?? '…'}
      </PrimaryButton>
    </div>
  );
}
